import {
	type Campaign,
	campaignApi,
	campaignHooks,
	CampaignsUI
} from 'features/campaigns'
import { useId, useState } from 'react'
import { useSwitcher } from 'shared/hooks'
import { modal } from 'shared/styles'
import {
	Button,
	type ContactsTable,
	ListSelect,
	LoadingButton,
	StackIcon
} from 'shared/ui'
import { ControlledDialog } from 'shared/ui/Dialog'

import css from './AddToCampaign.module.scss'

export function AddToCampaign({
	amount = 0,
	className,
	filters,
	unSelectAll
}: ContactsTable.Types.ActionsProps & {
	className?: string
}) {
	const switcher = useSwitcher(false)

	const [value, setValue] = useState<Campaign.Campaign | null>(null)

	const id = useId()

	const campaignQuery = campaignApi.useCampaignsPageQuery(
		{
			$filter: '(archived eq false)',
			$orderby: 'created desc',
			$take: 10
		},
		{
			skip: !switcher.isOn
		}
	)

	const [add, isLoading] = campaignHooks.useValidateAddContacts()

	function closeWithReset() {
		setValue(null)
		switcher.off()
	}

	return (
		<>
			<Button
				className={className}
				onClick={switcher.on}
				variant='bordered-dark'
			>
				<StackIcon icon='plus' />
				Add to campaign
			</Button>

			<ControlledDialog
				className={css.wrapper}
				onRequestClose={closeWithReset}
				open={switcher.isOn}
				rootClassName={css.root}
			>
				<button className={modal.close} onClick={closeWithReset} type='button'>
					<StackIcon icon='close' stroke='currentcolor' width={14} />
				</button>

				<form
					className={css.form}
					onSubmit={async event => {
						event.preventDefault()

						if (value) {
							await add(filters, value)
							unSelectAll()
							closeWithReset()
						}
					}}
				>
					<h2 className={css.title}>
						<label htmlFor={id}>Add contacts to campaign</label>
					</h2>

					<p
						className={css.lead}
					>{`Select the campaign to add ${amount} contacts`}</p>

					<ListSelect
						formatOptionLabel={CampaignsUI.Option}
						getOptionValue={option => option.id}
						getTag={camp => camp.name}
						inputId={id}
						isLoading={campaignQuery.isLoading}
						onChange={campaign => {
							setValue(campaign)
						}}
						options={campaignQuery.data?.documents ?? []}
						placeholder='Select campaigns'
						value={value}
					/>

					<LoadingButton
						disabled={value === null}
						isLoading={isLoading}
						type='submit'
					>
						Confirm
					</LoadingButton>
				</form>
			</ControlledDialog>
		</>
	)
}
